import { feature } from "topojson-client";
import type { GeometryCollection, Topology } from "topojson-specification";
// Natural Earth land outlines (public domain), loaded as separate files so the
// 546 KB detailed set is only fetched when someone zooms in.
import coarseUrl from "world-atlas/land-110m.json?url";
import detailedUrl from "world-atlas/land-50m.json?url";

/** Zoom at which the 1:50m outlines replace the 1:110m ones. */
export const DETAIL_ZOOM = 4;

type LandTopology = Topology<{ land: GeometryCollection }>;

export interface LandShape {
  /** Each ring as [lon, lat, lon, lat, …]. */
  rings: Float32Array[];
  /** Per ring: west, south, east, north — to skip rings outside the view. */
  boxes: Float32Array;
}

/** After a failed download, wait this long before the next attempt (the map redraws constantly). */
const RETRY_AFTER_MS = 30_000;

let coarse: LandShape | null = null;
let detailed: LandShape | null = null;
/** url → time of the last attempt; loaded urls stay here forever. */
const attempts = new Map<string, { at: number; loaded: boolean }>();
const listeners = new Set<() => void>();

function toShape(topology: LandTopology): LandShape {
  const rings: Float32Array[] = [];
  const addRing = (ring: number[][]) => {
    const coords = new Float32Array(ring.length * 2);
    // Land crossing the 180° meridian (Chukotka, Fiji) jumps from +180 to −180
    // between two points, which would draw a band across the whole map. Keep
    // longitudes continuous instead (e.g. 175 → 190); the map draws a copy
    // shifted by 360° so the part past 180° also shows on the other edge.
    let offset = 0;
    let previous: number | null = null;
    ring.forEach((point, i) => {
      const lon = point[0] ?? 0;
      if (previous !== null) {
        if (lon - previous > 180) offset -= 360;
        else if (previous - lon > 180) offset += 360;
      }
      previous = lon;
      coords[i * 2] = lon + offset;
      coords[i * 2 + 1] = point[1] ?? 0;
    });
    rings.push(coords);
  };
  for (const f of feature(topology, topology.objects.land).features) {
    const geometry = f.geometry;
    if (geometry?.type === "Polygon") geometry.coordinates.forEach(addRing);
    else if (geometry?.type === "MultiPolygon") geometry.coordinates.forEach((polygon) => polygon.forEach(addRing));
  }

  const boxes = new Float32Array(rings.length * 4);
  rings.forEach((ring, r) => {
    let west = Infinity;
    let south = Infinity;
    let east = -Infinity;
    let north = -Infinity;
    for (let i = 0; i < ring.length; i += 2) {
      const lon = ring[i] ?? 0;
      const lat = ring[i + 1] ?? 0;
      west = Math.min(west, lon);
      east = Math.max(east, lon);
      south = Math.min(south, lat);
      north = Math.max(north, lat);
    }
    boxes.set([west, south, east, north], r * 4);
  });
  return { rings, boxes };
}

function load(url: string, store: (shape: LandShape) => void): void {
  const previous = attempts.get(url);
  if (previous && (previous.loaded || Date.now() - previous.at < RETRY_AFTER_MS)) return;
  const attempt = { at: Date.now(), loaded: false };
  attempts.set(url, attempt);
  fetch(url)
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json() as Promise<LandTopology>;
    })
    .then((topology) => {
      attempt.loaded = true;
      store(toShape(topology));
      for (const listener of listeners) listener();
    })
    .catch((err: unknown) => {
      console.warn(`[map] land outlines not loaded from ${url}: ${err instanceof Error ? err.message : String(err)}`);
    });
}

/** Called whenever a set of outlines finishes loading. Returns an unsubscribe function. */
export function onLandLoaded(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The best outlines available for this zoom, starting their download on first
 * use. Null until the coarse set has loaded.
 */
export function landFor(zoom: number): LandShape | null {
  load(coarseUrl, (shape) => {
    coarse = shape;
  });
  if (zoom >= DETAIL_ZOOM) {
    load(detailedUrl, (shape) => {
      detailed = shape;
    });
    if (detailed) return detailed;
  }
  return coarse;
}
